import axios from 'axios';
import { getUserData } from './storageService';

const API_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/commission`
  : 'https://backend-c-0mxm.onrender.com/api/commission';

const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const user = getUserData();
  if (user) {
    config.headers.Authorization = `Bearer ${user.userId || user.token}`;
  }
  return config;
});

const handleError = (error) => {
  if (error.response) {
    return { message: error.response.data.message || 'Có lỗi xảy ra', status: error.response.status };
  }
  if (error.request) {
    return { message: 'Không thể kết nối đến server', status: 0 };
  }
  return { message: 'Có lỗi xảy ra', status: 0 };
};

export const getMyCurrentCommission = async () => {
  try {
    const response = await api.get('/my/current');
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

export const getMyCommissionHistory = async (months = 12) => {
  try {
    const response = await api.get('/my/history', { params: { months } });
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

export const adminGetAllCommissions = async (monthKey) => {
  try {
    const response = await api.get('/admin/all', { params: { monthKey } });
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

/**
 * So sánh doanh thu & hoa hồng giữa các tháng.
 * @param {String} mode - 'mtd' (cùng thời điểm trong tháng) | 'full' (trọn tháng)
 * @param {String} asOf - 'YYYY-MM-DD' neo ngày cắt (bỏ trống = hôm nay)
 */
export const adminGetMonthlyComparison = async ({ months = 6, mode = 'mtd', asOf } = {}) => {
  try {
    const response = await api.get('/admin/monthly-comparison', { params: { months, mode, asOf } });
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

export const adminSnapshotUser = async (userId, monthKey) => {
  try {
    const response = await api.post(`/admin/snapshot/${userId}`, { monthKey });
    return { success: true, data: response.data.data, message: response.data.message };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

export const adminSnapshotAll = async (monthKey) => {
  try {
    const response = await api.post('/admin/snapshot-all', { monthKey });
    return { success: true, data: response.data.data, message: response.data.message };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

// ==================== LEADER ENDPOINTS ====================

export const getLeaderCurrentCommission = async () => {
  try {
    const response = await api.get('/leader/current');
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

export const getLeaderTeamGMV = async (monthKey) => {
  try {
    const response = await api.get('/leader/team-gmv', { params: { monthKey } });
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

export const getLeaderCommissionHistory = async (months = 12) => {
  try {
    const response = await api.get('/leader/history', { params: { months } });
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

// ============================================================
// SỬA TAY SỐ LIỆU HOA HỒNG THÁNG (backend/models/CommissionAdjustment.js)
// ============================================================

/** Các điều chỉnh đang áp — để màn hoa hồng đánh dấu dòng nào đã sửa tay */
export const adminGetCommissionAdjustments = async (monthKey) => {
  try {
    const response = await api.get('/admin/adjustments', { params: { monthKey } });
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

/**
 * Sửa Tổng doanh thu và/hoặc ghi đè Hoa hồng của một người trong một tháng.
 * payload: { userId, monthKey, targetGMV?, commissionOverride?, reason }
 */
export const adminAdjustCommission = async (payload) => {
  try {
    const response = await api.post('/admin/adjust', payload);
    return {
      success: true, data: response.data.data, message: response.data.message,
      before: response.data.before, after: response.data.after,
      adjustedRevenues: response.data.adjustedRevenues, warnings: response.data.warnings,
    };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

/** Khôi phục về một mốc trong lịch sử sửa */
export const adminRollbackCommissionAdjustment = async (id, entryId) => {
  try {
    const response = await api.post(`/admin/adjust/${id}/rollback/${entryId}`);
    return { success: true, message: response.data.message, after: response.data.after, warnings: response.data.warnings };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

/** Hoàn nguyên hẳn: trả doanh thu về số GỐC, bỏ ghi đè hoa hồng, tính lại lương */
export const adminRevertCommissionAdjustment = async (id) => {
  try {
    const response = await api.post(`/admin/adjust/${id}/revert`);
    return { success: true, message: response.data.message, warnings: response.data.warnings };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};
